import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Layout } from '@/components/layout/Layout';
import { Link } from 'react-router-dom';
import { ChevronRight, Award, Globe, Users } from 'lucide-react';

const FOUNDER_IMAGE = 'https://customer-assets.emergentagent.com/job_parla-italiano-268/artifacts/e7q11rhz_foto%20mia%20pizzaiolo%20sfondo.JPG';

export default function AboutPage() {
  const { t } = useLanguage();

  const experiences = [
    { location: 'Italia', code: 'it' },
    { location: 'Isole Canarie', code: 'es' },
    { location: 'Francia', code: 'fr' },
    { location: 'Germania', code: 'de' },
    { location: 'Portogallo', code: 'pt' },
    { location: 'Malta', code: 'mt' },
    { location: 'Paesi Bassi', code: 'nl' },
    { location: 'Svezia', code: 'se' }
  ];

  const values = [
    {
      icon: Award,
      title: t.about.value1Title,
      desc: t.about.value1Desc
    },
    {
      icon: Globe,
      title: t.about.value2Title,
      desc: t.about.value2Desc
    },
    {
      icon: Users,
      title: t.about.value3Title,
      desc: t.about.value3Desc
    }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 md:py-32 relative" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div>
              <span className="text-primary uppercase tracking-widest text-sm font-medium mb-4 block">
                {t.about.subtitle}
              </span>
              <h1 className="text-4xl md:text-6xl font-serif text-foreground mb-6">
                {t.about.title}
              </h1>
              <p className="text-xl text-foreground mb-4">
                {t.about.intro}
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t.about.experience}
              </p>
              <blockquote className="text-lg text-muted-foreground italic border-l-2 border-primary pl-6 mb-8">
                "{t.about.mission}"
              </blockquote>

              {/* Experience Locations */}
              <div className="flex flex-wrap gap-3">
                {experiences.map((exp, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 bg-card border border-primary/10 px-4 py-2 rounded-sm"
                  >
                    <img 
                      src={`https://flagcdn.com/24x18/${exp.code}.png`} 
                      srcSet={`https://flagcdn.com/48x36/${exp.code}.png 2x`}
                      width="24" 
                      height="18" 
                      alt={exp.location}
                      className="object-cover rounded-[2px]"
                    />
                    <span className="text-sm text-foreground">{exp.location}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/5] rounded-sm overflow-hidden border border-primary/20 shadow-2xl">
                <img
                  src={FOUNDER_IMAGE}
                  alt="Giacomo - Fondatore Artisan Pizza Talent"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-primary/5 rounded-sm -z-10" />
              <div className="absolute -top-6 -right-6 w-32 h-32 border border-primary/20 rounded-sm -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-card relative noise-overlay" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-serif text-foreground mb-4">
              {t.about.valuesTitle}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t.about.valuesSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, idx) => (
              <div
                key={idx}
                className="bg-background border border-primary/10 hover:border-primary/30 p-8 rounded-sm card-hover text-center"
                data-testid={`value-card-${idx}`}
              >
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="text-primary" size={28} />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-3">
                  {value.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-24 md:py-32 relative" data-testid="expertise-section">
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url("https://static.prod-images.emergentagent.com/jobs/d9cf0590-fa7d-4aa8-865e-545c02b78803/images/b4bb4e2e5cda3431a49d162b8bacc248310ffbc0163b4329aac04582c82639f3.png")' }}
        />
        <div className="absolute inset-0 z-0 bg-black/70" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-serif text-foreground mb-6">
                {t.about.filterTitle} <span className="text-gradient-gold">{t.about.filterTitleAccent}</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t.about.filterDesc1}
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                {t.about.filterDesc2}
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full" />
                  <span className="text-foreground">{t.about.filterPoint1}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full" />
                  <span className="text-foreground">{t.about.filterPoint2}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full" />
                  <span className="text-foreground">{t.about.filterPoint3}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full" />
                  <span className="text-foreground">{t.about.filterPoint4}</span>
                </div>
              </div>
            </div>

            <div className="bg-card border border-primary/10 p-8 md:p-12 rounded-sm">
              <h3 className="text-2xl font-serif text-foreground mb-6">
                {t.about.servicesTitle}
              </h3>
              <div className="space-y-6">
                <div className="border-b border-primary/10 pb-6">
                  <h4 className="text-lg font-medium text-foreground mb-2">{t.about.service1Title}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t.about.service1Desc}
                  </p>
                </div>
                <div className="border-b border-primary/10 pb-6">
                  <h4 className="text-lg font-medium text-foreground mb-2">{t.about.service2Title}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t.about.service2Desc}
                  </p>
                </div>
                <div className="border-b border-primary/10 pb-6">
                  <h4 className="text-lg font-medium text-foreground mb-2">{t.about.service3Title}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t.about.service3Desc}
                  </p>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-foreground mb-2">{t.about.service4Title}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t.about.service4Desc}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-card relative noise-overlay" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-serif text-foreground mb-6">
            {t.about.ctaTitle} <span className="text-gradient-gold">{t.about.ctaTitleAccent}</span>?
          </h2>
          <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
            {t.about.ctaDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/per-pizzaioli"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold uppercase tracking-widest px-8 py-4 rounded-sm btn-glow hover:brightness-110 transition-all duration-300"
              data-testid="cta-chef"
            >
              {t.about.ctaChef}
              <ChevronRight size={18} />
            </Link>
            <Link
              to="/per-ristoratori"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-primary/40 text-primary font-semibold uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-primary/10 transition-all duration-300"
              data-testid="cta-restaurant"
            >
              {t.about.ctaRestaurant}
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
